import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Squiggle, SquiggleUnderline, FloatingBlob } from "@/components/Squiggle";

// Reusable Speelruimte-hero for kernpagina's.
// Warmte van Route A (belofte-italic), autoriteit van Route B (metrics/eyebrow),
// gedragen door de basisidentiteit van Route C (kronkellijnen + speelse kleuren).

export default function SpeelruimteBanner({
  eyebrow,
  headline,            // JSX allowed
  belofte,             // A-warmte, italic small kicker onder headline
  subhead,
  chips,               // array of strings (facts)
  metrics,             // B-scherpte: [{ label, value, sub }]
  ctaPrimary,          // { label, to, testid, icon? }
  ctaSecondary,        // { label, to, testid, external? }
  image,               // primary image url
  images,              // optional array (2-4) — overrides image
  tone = "coral",      // coral | sky | yellow
  align = "left",      // headline align
  "data-testid": testId = "speelruimte-banner",
}) {
  const accents = {
    coral: { primary: "#E06B3F", secondary: "#F0B98D", ambient: "#97B79D" },
    sky: { primary: "#97B79D", secondary: "#E06B3F", ambient: "#F0B98D" },
    yellow: { primary: "#F0B98D", secondary: "#E06B3F", ambient: "#97B79D" },
  }[tone];

  const ease = [0.22, 1, 0.36, 1];

  return (
    <section
      className="relative overflow-hidden bg-ll-cream text-ll-ink"
      data-testid={testId}
    >
      {/* Ambient shapes */}
      <FloatingBlob color={accents.secondary} size={200} className="top-14 -right-14 opacity-90" />
      <FloatingBlob color={accents.ambient} size={130} className="top-1/2 -left-10 opacity-80" delay={2} />

      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pt-12 sm:pt-20 pb-24 lg:pb-32 relative z-[1]">
        <div className={`grid grid-cols-1 ${images || image ? "lg:grid-cols-12" : ""} gap-10 lg:gap-14 items-center`}>
          <div className={images || image ? "lg:col-span-7" : ""}>
            {eyebrow && (
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1, ease }}
                className="text-xs uppercase tracking-[0.35em]"
                style={{ color: accents.primary }}
              >
                {eyebrow}
              </motion.p>
            )}

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.2, ease }}
              className={`font-heading font-light text-[44px] sm:text-6xl lg:text-[88px] leading-[0.96] tracking-tight text-ll-ink mt-8 ${align === "center" ? "text-center" : ""}`}
            >
              {headline}
            </motion.h1>

            {belofte && (
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.55, ease }}
                className="mt-6 font-heading italic text-xl sm:text-2xl leading-snug text-ll-coral max-w-2xl"
              >
                {belofte}
              </motion.p>
            )}

            {subhead && (
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.7, ease }}
                className="mt-8 max-w-2xl text-lg sm:text-xl text-ll-navy/80 leading-relaxed"
              >
                {subhead}
              </motion.p>
            )}

            {chips && chips.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.85, ease }}
                className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-ll-ink"
              >
                {chips.map((c) => (
                  <span key={c} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: accents.primary }} />
                    {c}
                  </span>
                ))}
              </motion.div>
            )}

            {(ctaPrimary || ctaSecondary) && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 1, ease }}
                className="mt-12 flex flex-col sm:flex-row gap-4"
              >
                {ctaPrimary && (
                  ctaPrimary.href ? (
                    <a
                      href={ctaPrimary.href}
                      target={ctaPrimary.external ? "_blank" : undefined}
                      rel={ctaPrimary.external ? "noreferrer" : undefined}
                      data-testid={ctaPrimary.testid}
                      className="group inline-flex items-center justify-between gap-4 bg-ll-ink text-ll-cream hover:bg-ll-coral rounded-full pl-7 pr-2 py-2 transition-all duration-300"
                    >
                      <span className="text-base font-medium flex items-center gap-2">
                        {ctaPrimary.icon}
                        {ctaPrimary.label}
                      </span>
                      <span className="text-ll-ink w-11 h-11 rounded-full flex items-center justify-center transition-colors shrink-0" style={{ background: accents.secondary }}>
                        <ArrowUpRight size={18} />
                      </span>
                    </a>
                  ) : (
                    <Link
                      to={ctaPrimary.to || "/"}
                      data-testid={ctaPrimary.testid}
                      className="group inline-flex items-center justify-between gap-4 bg-ll-ink text-ll-cream hover:bg-ll-coral rounded-full pl-7 pr-2 py-2 transition-all duration-300"
                    >
                      <span className="text-base font-medium flex items-center gap-2">
                        {ctaPrimary.icon}
                        {ctaPrimary.label}
                      </span>
                      <span className="text-ll-ink w-11 h-11 rounded-full flex items-center justify-center transition-colors shrink-0" style={{ background: accents.secondary }}>
                        <ArrowUpRight size={18} />
                      </span>
                    </Link>
                  )
                )}
                {ctaSecondary && (
                  ctaSecondary.href ? (
                    <a
                      href={ctaSecondary.href}
                      target={ctaSecondary.external ? "_blank" : undefined}
                      rel={ctaSecondary.external ? "noreferrer" : undefined}
                      data-testid={ctaSecondary.testid}
                      className="group inline-flex items-center gap-2 text-base font-medium text-ll-navy/90 hover:text-ll-coral transition-colors self-start sm:self-center pt-3"
                    >
                      <span className="link-underline">{ctaSecondary.label}</span>
                      <ArrowUpRight size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </a>
                  ) : (
                    <Link
                      to={ctaSecondary.to || "/"}
                      data-testid={ctaSecondary.testid}
                      className="group inline-flex items-center gap-2 text-base font-medium text-ll-navy/90 hover:text-ll-coral transition-colors self-start sm:self-center pt-3"
                    >
                      <span className="link-underline">{ctaSecondary.label}</span>
                      <ArrowUpRight size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </Link>
                  )
                )}
              </motion.div>
            )}

            {metrics && metrics.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.1, ease }}
                className="mt-14 grid grid-cols-3 gap-6 border-t border-ll-ink/15 pt-8 max-w-2xl"
              >
                {metrics.map((m, i) => (
                  <div key={m.label}>
                    <p className="chapter-num text-4xl sm:text-5xl" style={{ color: accents.primary }}>
                      {m.value}
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.22em] mt-2 text-ll-navy/60">{m.label}</p>
                    {m.sub && <p className="text-sm mt-1 text-ll-navy/70">{m.sub}</p>}
                  </div>
                ))}
              </motion.div>
            )}
          </div>

          {(images || image) && (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.4, ease }}
              className="lg:col-span-5 relative"
            >
              {images && images.length > 1 ? (
                <div className="grid grid-cols-6 grid-rows-6 gap-3 aspect-square">
                  <div className="col-span-4 row-span-4 rounded-[28px] overflow-hidden shadow-lg">
                    <img src={images[0]} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="col-span-2 row-span-3 rounded-[24px] overflow-hidden shadow-md" style={{ background: accents.ambient }}>
                    <img src={images[1]} alt="" className="w-full h-full object-cover mix-blend-multiply opacity-95" />
                  </div>
                  {images[2] && (
                    <div className="col-span-2 row-span-3 rounded-[24px] overflow-hidden shadow-md">
                      <img src={images[2]} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  {images[3] && (
                    <div className="col-span-4 row-span-2 rounded-[24px] overflow-hidden shadow-md">
                      <img src={images[3]} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
              ) : (
                <div className="relative aspect-[5/6] rounded-[32px] overflow-hidden shadow-lg">
                  <img src={image || images[0]} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <Squiggle color={accents.primary} className="absolute -top-6 -left-4 w-40 h-10 opacity-70" delay={0.9} duration={2} />
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
